import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material";
import { AkFeatureSelection } from "fe-web-components/autocomplete";
import { GenderValue, KahunCategories } from "shared";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { QualifiedFeature, Qualifier } from "fe-common/graphql";
import { RefineFeaturePanel } from "../refineFeature/refineFeaturePanel";
import { SectionTitle } from "../common/SectionMainTitle";
import { AkSuggestion } from "../../akAutoComplete";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { featureTo2Lines } from "fe-common/logic";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import AddIcon from "@mui/icons-material/Add";
import { useScreenSize } from "../util/useScreenSize";
import { ClearIcon } from "../common/buttons/clearIcon";

//region [[ Styles ]]
const ChiefComplaintPanelView = styled("div")(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    marginTop: "15px",
  },
}));
//endregion [[ Styles ]]

const FeatureSelectionWrapper = styled("div")(({ theme }) => ({
  marginBottom: "30px",
  backgroundColor: "white",
  display: "flex",
  width: "70%",

  [theme.breakpoints.up(MobileView)]: {
    "& .MuiInputBase-root": {
      fontSize: "23px",
      padding: theme.spacing(2),
    },
  },

  [theme.breakpoints.down(MobileView)]: {
    width: "100%",
    "& .MuiInputBase-root": {
      fontSize: "17px",
      width: "100%",
      fontWeight: 500,
      padding: theme.spacing(0.5),
    },
  },
}));

const SuggestionsList = styled("div")(({ theme }) => ({
  marginLeft: "0px",
  marginTop: "30px",
  paddingBottom: "100px",

  [theme.breakpoints.down(MobileView)]: {
    marginLeft: "0px",
    marginTop: "15px",
  },
}));

const SuggestionItem = styled("div")(({ theme }) => ({
  fontSize: "20px",
  color: RosebudColorMap.gray1,
  cursor: "pointer",
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "17px",
    marginTop: "-3px",
    fontWeight: 500,
  },
}));

const SuggestionIcon = styled("div")(({ theme }) => ({
  height: "48px",
  width: "48px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "5px",
  marginRight: "20px",
  backgroundColor: "white",
  border: "1px solid rgba(176, 176, 176, 0.7)",
  fontSize: "16px",
  color: "rgba(176, 176, 176, 0.9)",
  [theme.breakpoints.down(MobileView)]: {
    marginRight: "12px",
  },
}));

const SuggestionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  marginBottom: "10px",
}));

const SearchIconContainer = styled("div")(({ theme }) => ({
  padding: "5px",
  marginRight: "5px",
  marginLeft: "5px",
  opacity: 1,
  marginBottom: "2px",
  color: "black",
}));

const StyledSectionTitle = styled(SectionTitle)(({ theme }) => ({
  marginBottom: "10px",
  fontSize: "20px",
  fontWeight: 400,
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "22px",
    fontWeight: 500,
  },
}));

const RefineContainer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(10),
}));

//region [[ Props ]]

export interface ChiefComplaintPanelProps {
  className?: string;
  setChiefComplaint: (value: any) => void;
  removeChiefComplaint: (value: any) => void;
  chiefComplaint: QualifiedFeature;
  gender: GenderValue;
}

//endregion [[ Props ]]

//region [[ Functions ]]

const searchIcon = (
  <SearchIconContainer>
    <i className="far fa-search"></i>
  </SearchIconContainer>
);

const categories = [
  KahunCategories.SIGNS_SYMPTOMS,
  KahunCategories.KAHUN_SYMPTOMS,
  KahunCategories.SIGN,
];

//endregion [[ Functions ]]

export const ChiefComplaintPanel = ({ ...props }: ChiefComplaintPanelProps) => {
  const { chiefComplaint, removeChiefComplaint, setChiefComplaint } = props;
  const [suggestions, setSuggestions] = useState(null);
  const [chiefComplaintText, setChiefComplaintText] = useState(
    chiefComplaint ? featureTo2Lines(chiefComplaint)[0] : null,
  );
  const MAX_SUGGESTIONS = 5;
  const { isMobile } = useScreenSize();

  useEffect(() => {
    setChiefComplaintText(
      chiefComplaint ? featureTo2Lines(chiefComplaint)[0] : null,
    );
  }, [chiefComplaint]);

  const onChangeChiefComplaint = useCallback(
    (e, suggestion) => {
      props.setChiefComplaint(suggestion.value);

      evt({
        category: CATEGORY.AGENT_MAIN,
        action: ACTION.SELECT_CHIEF_COMPLAINT,
        label: suggestion.name,
      });
    },
    [props],
  );

  const onRefinement = useCallback(
    (qualifiers: Qualifier[]) => {
      setChiefComplaint({
        ...chiefComplaint,
        qualifiers,
      });
    },
    [setChiefComplaint, chiefComplaint],
  );

  const handleSuggestions = useCallback((suggestionsResp: AkSuggestion[]) => {
    if (suggestionsResp && suggestionsResp.length > 0) {
      setSuggestions(suggestionsResp.slice(0, MAX_SUGGESTIONS));
    } else if (suggestionsResp.length === 0) {
      setSuggestions([]);
    }
  }, []);

  const handleRemoveChiefComplaint = () => {
    removeChiefComplaint(chiefComplaint);
    setSuggestions(null);
  };

  return (
    <ChiefComplaintPanelView className={props.className}>
      <StyledSectionTitle>Chief Complaint</StyledSectionTitle>
      <FeatureSelectionWrapper>
        <AkFeatureSelection
          placeholder={"Chief Complaint"}
          inputProps={{ "aria-label": "search kahun medical database" }}
          autoFocus={isMobile}
          disabled={chiefComplaintText !== null}
          searchIcon={searchIcon}
          onChange={onChangeChiefComplaint}
          onSuggestions={handleSuggestions}
          searchOptions={{ categories: categories, gender: props.gender }}
          selectedValue={chiefComplaintText}
          hideResults
        />
        <ClearIcon onClick={handleRemoveChiefComplaint} />
      </FeatureSelectionWrapper>
      {!chiefComplaint && (
        <SuggestionsList>
          {suggestions &&
            suggestions.map((suggestion, index) => (
              <SuggestionContainer
                key={`suggestion-${index}`}
                onClick={(e) => {
                  onChangeChiefComplaint(e, suggestion);
                }}
              >
                <SuggestionIcon>
                  <AddIcon />
                </SuggestionIcon>
                <SuggestionItem>{suggestion.name}</SuggestionItem>
              </SuggestionContainer>
            ))}
        </SuggestionsList>
      )}
      {chiefComplaint && (
        <RefineContainer>
          <RefineFeaturePanel
            showDuration={true}
            onChange={onRefinement}
            qf={chiefComplaint}
            selectedQualifiers={
              chiefComplaint && chiefComplaint.qualifiers
                ? chiefComplaint.qualifiers
                : []
            }
            showTitle={false}
          />
        </RefineContainer>
      )}
    </ChiefComplaintPanelView>
  );
};
